import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import SmetaTabs from "../../../Tools/Tabs/SmetaTabs";
import { SmetaTabItem } from "../../../Tools/Tabs/PinIcons";
import SmetaSplitter from "../../../Tools/Splitter/SmetaSplitter";
import SmetaSplitterItem from "../../../Tools/Splitter/SmetaSplitterItem";
import LoadingMask from "../../../NotWorkArea(Side&Head)/LoadingMask";

type DetailItems = DocumentDetailItem[] | Record<string, DocumentDetailItem>;
const EMPTY_HIDDEN_TABS: Record<string, boolean> = {};

export interface DocumentDetailItem {
  DocCfgID?: string | number;
  Name?: string;
  Hidden?: string | boolean;
  TextField?: string | null;
  items?: DetailItems;
  docCfgID?: string | number;
  name?: string;
  hidden?: string | boolean;
  textField?: string | null;
  [key: string]: unknown;
}

export interface DocumentDetailsNode {
  items?: DetailItems;
  Details?: DocumentDetailsNode;
}

export interface DocumentDetailTab {
  key: string;
  docCfgID: string;
  name: string;
  textField: string | null;
  item: DocumentDetailItem;
  parents: DocumentDetailTabParent[];
}

export interface DocumentDetailTabParent {
  key: string;
  docCfgID: string;
  name: string;
  item: DocumentDetailItem;
}

export interface DocumentDetailContentContext {
  /** Вкладка, для которой сейчас собираем контент */
  tab: DocumentDetailTab;
  /** Текущий уровень детализации, у конечного грида совпадает с tab */
  detail: DocumentDetailTab | DocumentDetailTabParent;
  /** Позиция промежуточного грида, у конечного грида ее нет */
  parentIndex?: number;
  masterGrid?: unknown;
}

type GridPanelCleanup = void | (() => void);

interface DetailGridPanelProps {
  context: DocumentDetailContentContext;
  id: string;
  onFocus?: (panel: HTMLDivElement, context: DocumentDetailContentContext) => void;
  onMount?: (
    panel: HTMLDivElement,
    context: DocumentDetailContentContext,
  ) => GridPanelCleanup | Promise<GridPanelCleanup>;
}

export interface SectionDocumentDetailsProps {
  /** Результат GetDocumentLayout или сразу узел Details */
  documentLayout?: DocumentDetailsNode;
  masterGrid?: unknown;
  tabIndex?: number;
  initialTabIndex?: number;
  hiddenTabs?: Record<string, boolean>;
  detailsSplittersSizes?: Record<string, number>;
  loading?: boolean;
  className?: string;
  onTabIndexChange?: (index: number, tab: DocumentDetailTab, tabElement: HTMLElement) => void | Promise<void>;
  onSplitterResize?: (splitter: HTMLElement, tab: DocumentDetailTab) => void;
  onGridFocus?: (panel: HTMLDivElement, context: DocumentDetailContentContext) => void;
  /**
   * Создает legacy-грид внутри готовой React-панели
   * Возвращенная функция вызывается при удалении панели
   */
  onGridPanelMount?: DetailGridPanelProps["onMount"];
  /** Позволяет отрисовать React-грид вместо legacy-панели */
  renderGrid?: (context: DocumentDetailContentContext) => ReactNode;
  /**
   * Отрисовывает текстовое поле или модуль обычным JSX
   * Chat, SectionDocuments и редакторы больше не требуют отдельного createRoot
   */
  renderModule?: (context: DocumentDetailContentContext) => ReactNode;
}

const getItems = (node?: DocumentDetailsNode | DocumentDetailItem): DocumentDetailItem[] => {
  // Сервер может прислать items как массив или как объект
  if (!node) return [];
  const items = node.items;
  if (!items) return [];
  return Array.isArray(items) ? items : Object.values(items);
};

const getDocCfgID = (item: DocumentDetailItem) => String(item.DocCfgID ?? item.docCfgID ?? "");
const getName = (item: DocumentDetailItem) => item.Name ?? item.name ?? "";
const getTextField = (item: DocumentDetailItem) => item.TextField ?? item.textField ?? null;
const isHidden = (item: DocumentDetailItem) =>
  item.Hidden === "1" || item.Hidden === true || item.hidden === "1" || item.hidden === true;

/** Разворачивает дерево Details в тот же порядок, который виден во вкладках */
export const flattenDocumentDetails = (
  details?: DocumentDetailsNode,
  hiddenTabs: Record<string, boolean> = {},
): DocumentDetailTab[] => {
  const result: DocumentDetailTab[] = [];

  const visit = (items: DocumentDetailItem[], parents: DocumentDetailTabParent[]) => {
    items.forEach((item) => {
      const docCfgID = getDocCfgID(item);
      if (!docCfgID || isHidden(item) || hiddenTabs[docCfgID]) return;

      // Путь из DocCfgID остается стабильным при скрытии и перестановке соседних вкладок
      const path = [...parents.map((parent) => parent.docCfgID), docCfgID];
      const key = path.join("/");
      const tab: DocumentDetailTab = {
        key,
        docCfgID,
        name: getName(item),
        textField: getTextField(item),
        item,
        parents,
      };
      result.push(tab);

      visit(getItems(item), [
        ...parents,
        {
          key,
          docCfgID,
          name: tab.name,
          item,
        },
      ]);
    });
  };

  visit(getItems(details), []);
  return result;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const DetailGridPanel = ({ context, id, onFocus, onMount }: DetailGridPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const mountCallbackRef = useRef(onMount);
  const contextRef = useRef(context);

  // Берем свежие данные без пересоздания уже работающего legacy-грида
  mountCallbackRef.current = onMount;
  contextRef.current = context;

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    // Этот атрибут нужен старым обработчикам контекстного меню грида
    panel.setAttribute("rcsgrid_panel", "");
    let disposed = false;
    let cleanup: GridPanelCleanup;

    Promise.resolve(mountCallbackRef.current?.(panel, contextRef.current)).then((mountCleanup) => {
      // Асинхронная инициализация может закончиться уже после закрытия вкладки
      if (disposed) mountCleanup?.();
      else cleanup = mountCleanup;
    });

    return () => {
      disposed = true;
      cleanup?.();
    };
  }, [id]);

  return (
    <div
      id={id}
      ref={panelRef}
      tabIndex={0}
      onFocus={() => panelRef.current && onFocus?.(panelRef.current, context)}
      style={{ width: "100%", height: "100%", minWidth: 0, overflow: "hidden", position: "relative" }}
    />
  );
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const SectionDocumentDetails = ({
  documentLayout,
  masterGrid,
  tabIndex,
  initialTabIndex = 0,
  hiddenTabs = EMPTY_HIDDEN_TABS,
  detailsSplittersSizes,
  loading = false,
  className,
  onTabIndexChange,
  onSplitterResize,
  onGridFocus,
  onGridPanelMount,
  renderGrid,
  renderModule,
}: SectionDocumentDetailsProps) => {
  const [innerTabIndex, setInnerTabIndex] = useState(initialTabIndex);
  const details = documentLayout?.Details ?? documentLayout;
  const tabs = useMemo(() => flattenDocumentDetails(details, hiddenTabs), [details, hiddenTabs]);

  // tabIndex делает компонент управляемым, без него выбранную вкладку храним здесь
  const selectedIndex = tabIndex ?? innerTabIndex;
  const safeSelectedIndex = tabs.length === 0 ? 0 : Math.max(0, Math.min(selectedIndex, tabs.length - 1));

  useEffect(() => {
    // После скрытия вкладок выбранный индекс не должен выйти за границы списка
    if (tabIndex === undefined && innerTabIndex !== safeSelectedIndex) setInnerTabIndex(safeSelectedIndex);
  }, [innerTabIndex, safeSelectedIndex, tabIndex]);

  const renderGridContent = (context: DocumentDetailContentContext, id: string) => {
    // Новый React-грид можно передать снаружи, иначе оставляем контейнер для legacy-грида
    if (renderGrid) return renderGrid(context);
    return <DetailGridPanel id={id} context={context} onFocus={onGridFocus} onMount={onGridPanelMount} />;
  };

  const renderTabContent = (tab: DocumentDetailTab) => {
    // В конечной панели может быть грид, редактор или вложенный SectionDocuments
    const targetContext: DocumentDetailContentContext = { tab, detail: tab, masterGrid };
    const targetContent =
      tab.textField !== null
        ? renderModule?.(targetContext)
        : renderGridContent(targetContext, `gridpanel${tab.docCfgID}`);

    const parentsSize = detailsSplittersSizes?.[tab.docCfgID] ?? 112;
    // Сохраненный размер делим между всеми промежуточными уровнями детализации
    const parentSize = tab.parents.length ? `${parentsSize / tab.parents.length}px` : undefined;

    const handleResize = (splitter: HTMLElement) => onSplitterResize?.(splitter, tab);

    return (
      <SmetaSplitter
        style={{ width: "100%", height: "100%", borderTop: "1px solid rgb(208, 208, 208)", flex: "1 1" }}
        orientation="vertical"
        onExpand={handleResize}
        onCollapse={handleResize}
        onResizeEnd={handleResize}
        hideSplitter={tab.parents.length === 0}
        primaryIndex="end"
        blockResize={false}
      >
        {/* Каждый родитель становится отдельной панелью перед конечной детализацией */}
        {tab.parents.map((parent, parentIndex) => {
          const context: DocumentDetailContentContext = { tab, detail: parent, parentIndex, masterGrid };
          return (
            <SmetaSplitterItem key={parent.key} size={parentSize}>
              {renderGridContent(context, `gridpanelDetails${tab.docCfgID}-${parentIndex}`)}
            </SmetaSplitterItem>
          );
        })}
        <SmetaSplitterItem>
          <div
            id={tab.textField !== null ? `gridpanel${tab.docCfgID}` : undefined}
            style={{ width: "100%", height: "100%", minWidth: 0, overflow: "hidden", position: "relative" }}
          >
            {targetContent}
          </div>
        </SmetaSplitterItem>
      </SmetaSplitter>
    );
  };

  return (
    <div className={className} style={{ width: "100%", height: "100%", minHeight: 0, position: "relative" }}>
      <LoadingMask bool={!loading} />
      {tabs.length > 0 && (
        <>
          {/* Контент посещенных вкладок остается смонтированным внутри SmetaTabs */}
          <SmetaTabs
            position="bottom"
            tabIndex={safeSelectedIndex}
            style={{ width: "100%", height: "100%", minHeight: 0 }}
            onTabIndexChange={async (index, tabElement, data) => {
              const tab = data as DocumentDetailTab;
              if (tabIndex === undefined) setInnerTabIndex(index);
              await onTabIndexChange?.(index, tab, tabElement);
            }}
          >
            {tabs.map((tab) => (
              <SmetaTabItem
                key={tab.key}
                id={`details${tab.docCfgID}`}
                label={tab.name}
                data={tab}
                style={{ width: "100%", height: "100%", padding: 0, overflow: "hidden" }}
              >
                {renderTabContent(tab)}
              </SmetaTabItem>
            ))}
          </SmetaTabs>
        </>
      )}
    </div>
  );
};

export default SectionDocumentDetails;
